<script setup lang="ts">
import { useThrottleFn, useEventListener } from '@vueuse/core'
import { ActionFields } from '~/components/cms/Action/actionHelpers'
import { Variants } from '~/components/ui/Button.vue'

export type QuickLinksProp = {
  fields?: {
    actions?: ActionFields[]
    supportingData?: {
      home?: {
        variant?: { value: Variants }
      }
    }
    disableActiveState?: boolean
  }
  activeLinks?: ActionFields[]
  disableAutoScrollOnMount?: boolean
  disableActiveState?: boolean
}

const props = defineProps<QuickLinksProp>()
const emit = defineEmits(['changeCategory'])

const linkWrapper = ref<HTMLElement | null>(null)
const scrollOffsetLeft = ref(0)
const scrollOffsetRight = ref(0)
const scrollBarWidth = ref(0)
const windowInnerWidth = ref(0)
const isMousePressed = ref(false)
const mouseStartPosition = ref(0)
const scrollStartPosition = ref(0)

const isQuickLinks = computed(() => props.fields?.supportingData?.home?.variant?.value === 'quickLinks')
const someHaveImage = computed(() => props.fields?.actions?.some((action) => action?.fields?.quickLinkPicture))
const linkContainerStyle = computed(() => {
  const availableScrollSpace = (linkWrapper.value?.scrollWidth ?? 0) - scrollBarWidth.value
  return availableScrollSpace <= 12 ? { overflowX: 'hidden' as const } : undefined
})

const setScrollPosition = () => {
  if (!linkWrapper.value) return

  scrollBarWidth.value = linkWrapper.value.offsetWidth
  scrollOffsetLeft.value = linkWrapper.value.scrollLeft
  scrollOffsetRight.value = linkWrapper.value.scrollWidth - linkWrapper.value.scrollLeft - scrollBarWidth.value
}

const scrollToActiveLink = useThrottleFn(() => {
  if (!linkWrapper.value || window.innerWidth === windowInnerWidth.value) return

  windowInnerWidth.value = window.innerWidth
  const activeLinkElement = linkWrapper.value.querySelector('.quick-links__action--active') as HTMLElement

  if (activeLinkElement) {
    const activeLinkWidth = activeLinkElement.offsetWidth
    const activeLinkhorizontalOffset = activeLinkElement.getBoundingClientRect().right
    linkWrapper.value.scrollLeft =
      activeLinkhorizontalOffset + linkWrapper.value.scrollLeft - window.innerWidth / 2 - activeLinkWidth / 2
  }
}, 100)

const mouseDown = (event: MouseEvent) => {
  if (!linkWrapper.value) return

  isMousePressed.value = true
  mouseStartPosition.value = event.pageX - linkWrapper.value.offsetLeft
  scrollStartPosition.value = linkWrapper.value.scrollLeft
}

const mouseMove = (event: MouseEvent) => {
  if (!isMousePressed.value || !linkWrapper.value) return

  const mousePosition = event.pageX - linkWrapper.value.offsetLeft
  linkWrapper.value.scrollLeft = scrollStartPosition.value - mousePosition + mouseStartPosition.value
}

const resetMouseState = () => {
  isMousePressed.value = false
}

const redirectToCategory = (action: ActionFields) => {
  if (action.fields?.url) window.location.href = action.fields?.url
}

const isActive = (id?: string) => {
  if (props.disableActiveState) return false
  return props.activeLinks?.some((action) => action.sys?.id === id)
}

onMounted(() => {
  if (linkWrapper.value) {
    useEventListener(linkWrapper.value, 'scroll', setScrollPosition)
    useEventListener(window, 'resize', setScrollPosition)
    useEventListener(window, 'resize', scrollToActiveLink)

    setScrollPosition()

    if (!props.disableAutoScrollOnMount) {
      nextTick(scrollToActiveLink)
    }
  }
})
</script>

<template>
  <div class="quick-links">
    <ul
      ref="linkWrapper"
      class="quick-links__links-container"
      :class="{ 'quick-links__links-container--image': isQuickLinks && someHaveImage }"
      :style="linkContainerStyle"
      @mouseleave="resetMouseState"
      @mouseup="resetMouseState"
      @mousedown="mouseDown"
      @mousemove="mouseMove"
    >
      <li
        v-for="action in props.fields?.actions"
        :key="action.sys?.id"
        class="quick-link__links-wrapper"
        :class="{
          'quick-link__links-wrapper focusable': isQuickLinks && someHaveImage,
          'quick-link__links-wrapper--no-image': !action.fields?.quickLinkPicture,
          'quick-link__links-wrapper--active': isActive(action.sys?.id),
        }"
        tabindex="0"
        @keyup.enter="redirectToCategory(action)"
        @mousedown.prevent
      >
        <div
          v-if="isQuickLinks && action.fields?.quickLinkPicture"
          @click="redirectToCategory(action)"
          class="full-width-picture"
        >
          <Picture v-bind="action.fields?.quickLinkPicture" />
        </div>
        <div
          v-else-if="isQuickLinks && someHaveImage"
          class="full-width-picture quick-links--no-image"
          @click="redirectToCategory(action)"
        ></div>
        <Anchor
          :url="action.fields?.url"
          class="quick-links__action"
          :class="{ 'quick-links__action--active': isActive(action.sys?.id) }"
        >
          {{ action.fields?.text }}
        </Anchor>
      </li>
    </ul>
    <div v-if="scrollOffsetLeft > 1" class="quick-links__left-overlay" />
    <div v-if="scrollOffsetRight > 1" class="quick-links__right-overlay" />
  </div>
</template>

<style lang="postcss">
.quick-links {
  ::-webkit-scrollbar {
    display: none;
    height: 0;
    width: 0;
    background: transparent;
  }

  position: relative;
  display: flex;
  justify-content: center;
  max-width: 100%;
  width: 100%;

  .full-width-picture {
    width: 8.563rem;
    aspect-ratio: 5 / 4;
    margin-bottom: var(--space-30);
    cursor: pointer;
    overflow: hidden;
    position: relative;
  }

  .full-width-picture::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--border-dark-30);
    transition: width 0.3s ease;
  }

  .full-width-picture .picture__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .full-width-picture.quick-links--no-image {
    background-color: var(--neutral-400);
  }

  .quick-links__links-container {
    gap: var(--space-30);
    display: flex;
    flex-flow: row nowrap;
    max-width: 100%;
    overflow-x: overlay;
    user-select: none;
    height: 100%;
    list-style-type: none;
    margin: 0;
    padding-bottom: var(--space-10);
    padding-left: 0;

    &.quick-links__links-container--image {
      gap: var(--space-10);
    }
  }

  .quick-link__links-wrapper {
    text-align: left;
    max-width: min-content;
  }

  .quick-link__links-wrapper--no-image {
    white-space: nowrap;
  }

  .quick-links__links-container--image .quick-link__links-wrapper--no-image {
    margin-left: var(--space-10);
    margin-right: var(--space-20);
  }

  .quick-links__links-container--image .quick-link__links-wrapper--no-image:first-child {
    margin-left: 0;
  }

  .quick-links__action {
    position: relative;
    display: inline-block;
    cursor: pointer;
    text-decoration: none;
    font-size: var(--text-small);
    margin: 0;
    white-space: nowrap;
  }

  .quick-links__action:focus-visible {
    outline: var(--outline-accessibility);
  }

  .quick-links__links-container:not(.quick-links__links-container--image) {
    .quick-links__action {
      border: 1px solid var(--border-neutral-20);
      padding: var(--space-40) var(--space-50);
      transition:
        border-color 0.2s ease,
        background-color 0.2s ease;
    }

    .quick-links__action:hover {
      text-decoration: none;
      border-color: var(--text-link-hover);
    }

    .quick-links__action--active {
      color: var(--text-primary);
      border-color: var(--border-neutral-20);
      background-color: var(--surface-light-20);
      font-weight: bold;

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: -1px;
        right: -1px;
        height: 2px;
        background-color: var(--border-dark-10);
      }
    }
  }

  .quick-links__links-container--image {
    .quick-links__action {
      white-space: normal;
      padding-right: var(--space-30);
      transition: color 0.1s ease 0.1s;
    }

    .quick-link__links-wrapper:hover:not(.quick-link__links-wrapper--active) {
      .full-width-picture::after {
        width: 100%;
      }

      .quick-links__action {
        color: var(--text-link-hover);
      }
    }

    .quick-link__links-wrapper:hover .full-width-picture .picture__img,
    .quick-link__links-wrapper:focus .full-width-picture .picture__img {
      transform: scale(1.1);
    }

    .quick-links__action::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 0;
      height: 2px;
      background-color: var(--text-link-hover);
      transition: width 0.3s ease;
    }

    .quick-link__links-wrapper--active .full-width-picture::after {
      width: 100%;
    }

    .quick-links__action--active {
      font-weight: bold;
    }
  }

  .quick-links__left-overlay,
  .quick-links__right-overlay {
    width: var(--space-80);
    height: 100%;
    position: absolute;
    top: 0;
    @media (--mobile-only) {
      width: var(--space-70);
    }
    @media (--tablet) {
      width: var(--space-60);
    }
  }

  .quick-links__left-overlay {
    left: 0;
    background: linear-gradient(270deg, rgba(255, 255, 255, 0), #ffffff);
  }

  .quick-links__right-overlay {
    right: 0;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0), #ffffff);
  }
}
</style>
